"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type {
  CUserAccountsContent,
  CUserAccountsLangSlice,
  CUserAccountsSession,
  CUserAccountsUserGroup,
} from "@/lib/api/cms-catalog";
import type { FrontendUserSession } from "@/lib/api/session";
import {
  Field,
  Input,
  Select,
  Checkbox,
  FormStatus,
  SubmitButton,
  PasswordStrengthMeter,
  buildPasswordRegex,
  type PasswordStrengthLabels,
  type PasswordRequirementsInput,
} from "@/components/ui/Form";
import { useLocale, useT } from "@/lib/i18n/context";
import { submitSsoPlant } from "@/lib/sso/plant";
import { OAuthButtons } from "@/components/shared/OAuthButtons/OAuthButtons";
import { LogingovChooser, LogingovPendingLinker } from "@/components/shared/Logingov/Logingov";
import { cUserAccountsTranslations, type CUserAccountsTranslations } from "./CUserAccounts.i18n";
import { ProfileTabs } from "./ProfileTabs";
import { ProcedureGuard } from "./ProcedureGuard";
import { MfaGuard } from "./MfaGuard";
import { AuthMfaModal } from "./AuthMfaModal";
import styles from "./CUserAccounts.module.css";

/**
 * CUserAccounts — visitor view komponentu `c_user_accounts`.
 *
 * **Iteracja 1 — Auth core:**
 *   - Logged-out -> login form + opcjonalny link "Załóż konto" (gating po
 *     `config.user_registration_enabled`)
 *   - Logged-in -> welcome message + button "Wyloguj"
 *   - Register tab -> form rejestracyjny (dropdown grupy + email + hasło x2 +
 *     opcjonalne checkboxy zgód)
 *
 * **Out of scope iter. 1** (przyszłe iteracje):
 *   - Profile data (`dataForm`/`updateData`) — 3 zakładki personal/company/billing
 *   - Change password / change email / delete account
 *   - 2FA / OAuth (Facebook, Google)
 *   - Employee list / art view
 *
 * **Backend integration:**
 *   - `content.session` = SSR snapshot z `getApiContent()` (null gdy visitor)
 *   - `content.endpoints` = pełne URL-e REST (login/logout/register/verify) —
 *     React-CMS używa proxy `/api/iiapi/...` (centralna obsługa user-mgmt,
 *     mirror backend `IIAPI_CONTROLLER`) żeby cookie management zostać po
 *     stronie Next.js (httpOnly)
 *   - `content.lang` = stringi z `c_user_accounts_{lang}.lang` (backend = SoT)
 *
 * **UI primitives:** używamy `@/components/ui/Form` (Field/Input/Select/
 * Checkbox/FormStatus) — wszystkie pola dziedziczą brand tokens.
 *
 * **Single-page tabs (login ↔ register):** użytkownik przełącza zakładki bez
 * nawigacji. Komponent trzyma `tab` w local state. Po sukces login lub
 * register-with-auto-login -> `router.refresh()` żeby chrome (header, menu)
 * dostały świeżą session info z SSR.
 */
export function CUserAccounts({ content, userSession }: CUserAccountsProps) {
  const t = useT(cUserAccountsTranslations);
  const searchParams = useSearchParams();
  const action = searchParams?.get("action");

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.empty}</div>
      </div>
    );
  }

  /* Change-email landings — działa BEZ login (token-only auth). 3-stage flow:
     - approve (klika OLD): wyraża zgodę -> backend wysyła confirm do NEW
     - confirm (klika NEW): finalizuje zmianę + invalidate sessions
     - cancel (klika OLD): anuluje pending change w obu stanach */
  if (
    action === "approve-change-email" ||
    action === "confirm-change-email" ||
    action === "cancel-change-email"
  ) {
    return <ChangeEmailLanding action={action} />;
  }

  /* Delete-account landings — działa BEZ login (token-only auth). 2-action flow:
     - delete-account: finalizuje soft delete + invalidate sessions
     - cancel-delete-account: anuluje pending delete (defense vs session hijack) */
  if (action === "delete-account" || action === "cancel-delete-account") {
    return <DeleteAccountLanding action={action} />;
  }

  /* Verify-account landing — user klika link w mailu aktywacyjnym, parametry
     checksum i user_id w query string (backend substytuje placeholdery
     {checksum}/{id_user} przez `IUSER::quickRegister` + custom_verify_url).
     Działa BEZ login (token-only auth). */
  if (action === "verify-account") {
    return <VerifyAccountLanding />;
  }

  /* Effective session — SSR-injected (`userSession` prop z `page.tsx`)
     wygrywa nad backend payload (`content.session`). Backend `getApiContent`
     wraz z REST nie ma aktywnej sesji (`IUSER::isLogged()` jest false bo
     adapter pattern przepuszcza tylko Bearer-token sesje, a publiczne
     `pageBootstrap` ich nie wysyła) — więc `content.session` jest tu zawsze
     null. Wstrzyknięta sesja z prop'a uwzględnia parent-domain `IMPERIAL`
     SSO cookie (admin) ORAZ `imperial_frontend_session` cookie (visitor
     zalogowany przez /api/iiapi/login). */
  const loggedIn = userSession !== null || content.session?.logged_in;

  if (loggedIn) {
    return <LoggedInView content={content} userSession={userSession} t={t} />;
  }

  return <LoggedOutView content={content} t={t} />;
}

/* ─── Change-email landing (confirm/cancel from email link) ──────────── */

/**
 * Auto-calls backend confirm/cancel endpoint on mount based on URL action.
 * Pokazuje loader -> success/error FormStatus. Po confirm: button "Zaloguj się
 * ponownie" (bo wszystkie sesje wylogowane). Po cancel: button "Wróć do konta".
 */
function ChangeEmailLanding({
  action,
}: {
  action: "approve-change-email" | "confirm-change-email" | "cancel-change-email";
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const token = searchParams?.get("token") ?? "";
  /* user_id w URL teraz ZAWSZE encrypted (IENCRYPTION::urlsafeEncode -> string).
     Backend dekoduje przez decryptUserId helper. Frontend traktuje opaque. */
  const userIdEncrypted = searchParams?.get("user_id") ?? "";

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (!token || !userIdEncrypted) {
      setStatus("error");
      setMessage("Nieprawidłowy link — brakuje parametrów token lub user_id.");
      return;
    }

    let cancelled = false;
    (async () => {
      const endpoint =
        action === "approve-change-email"
          ? "/api/iiapi/user/change-email/approve"
          : action === "confirm-change-email"
            ? "/api/iiapi/user/change-email/confirm"
            : "/api/iiapi/user/change-email/cancel";

      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, id_user: userIdEncrypted }),
        });
        const body = (await res.json().catch(() => null)) as {
          ok?: boolean;
          message?: string;
          email_changed?: boolean;
          new_email_masked?: string;
          cancelled?: boolean;
          approved?: boolean;
          confirm_email_sent?: boolean;
        } | null;

        if (cancelled) return;

        if (!res.ok || !body?.ok) {
          setStatus("error");
          setMessage(body?.message ?? "Operacja nie powiodła się.");
          return;
        }

        setStatus("success");
        if (action === "approve-change-email") {
          setMessage(
            `Twoja zgoda została zapisana. Email weryfikacyjny został wysłany na nowy adres${body.new_email_masked ? ` (${body.new_email_masked})` : ""}. Nowy właściciel musi kliknąć link „Potwierdź" w ciągu 1 godziny aby zmiana weszła w życie. Jeśli wiadomość nie dotarła w ciągu kilku minut — sprawdź folder SPAM (wiadomości niechciane).`,
          );
        } else if (action === "confirm-change-email") {
          setMessage(
            `Email zmieniony pomyślnie${body.new_email_masked ? ` na ${body.new_email_masked}` : ""}. Wszystkie sesje na innych urządzeniach zostały wylogowane — zaloguj się ponownie używając nowego adresu.`,
          );
        } else {
          setMessage(
            "Zmiana adresu email została anulowana. Twoje konto jest bezpieczne. Jeśli to nie Ty zainicjowałeś zmianę — rozważ zmianę hasła.",
          );
        }
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMessage(e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [action, token, userIdEncrypted]);

  const title =
    action === "approve-change-email"
      ? "Wyrażanie zgody na zmianę emaila"
      : action === "confirm-change-email"
        ? "Potwierdzanie zmiany emaila"
        : "Anulowanie zmiany emaila";

  const buttonLabel =
    action === "confirm-change-email"
      ? "Zaloguj się ponownie"
      : "Wróć do konta";

  return (
    <div className={styles.root}>
      <div className={styles.form}>
        <h2 className={styles.formTitle}>{title}</h2>

        {status === "loading" && (
          <FormStatus variant="info">Przetwarzanie…</FormStatus>
        )}
        {status === "success" && (
          <FormStatus variant="success">{message}</FormStatus>
        )}
        {status === "error" && (
          <FormStatus variant="error">{message}</FormStatus>
        )}

        {status !== "loading" && (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => {
              /* Po confirm: redirect na /konto bez query (wymusza re-login bo
                 sessions wiped). Po approve/cancel: wraca na /konto. */
              router.push(window.location.pathname);
            }}
          >
            {buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Delete-account landing (confirm/cancel from email link) ────────── */

/**
 * Auto-calls backend confirm/cancel delete endpoint on mount based on URL action.
 * Pokazuje loader -> success/error FormStatus. Po confirm: button "Wróć do strony
 * głównej" (konto usunięte, wylogowane). Po cancel: button "Wróć do konta".
 */
function DeleteAccountLanding({
  action,
}: {
  action: "delete-account" | "cancel-delete-account";
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const token = searchParams?.get("token") ?? "";
  const userIdEncrypted = searchParams?.get("user_id") ?? "";

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("");
  const [maskedEmail, setMaskedEmail] = useState<string>("");

  useEffect(() => {
    if (!token || !userIdEncrypted) {
      setStatus("error");
      setMessage("Nieprawidłowy link — brakuje parametrów token lub user_id.");
      return;
    }

    let cancelled = false;
    (async () => {
      const endpoint =
        action === "delete-account"
          ? "/api/iiapi/user/delete-account/confirm"
          : "/api/iiapi/user/delete-account/cancel";

      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, id_user: userIdEncrypted }),
        });
        const body = (await res.json().catch(() => null)) as {
          ok?: boolean;
          message?: string;
          account_deleted?: boolean;
          email_masked?: string;
          sessions_invalidated?: boolean;
          cancelled?: boolean;
        } | null;

        if (cancelled) return;

        if (!res.ok || !body?.ok) {
          setStatus("error");
          setMessage(body?.message ?? "Operacja nie powiodła się.");
          return;
        }

        setStatus("success");
        if (action === "delete-account") {
          setMaskedEmail(body.email_masked ?? "");
          setMessage(
            `Twoje konto zostało usunięte. Wszystkie sesje na innych urządzeniach zostały wylogowane. Dziękujemy za wspólny czas — żałujemy, że odchodzisz.`,
          );
          /* HARD navigation - `router.refresh()` tylko inwaliduje server cache
             Next.js i re-renderuje server components, ale CLIENT state (np.
             pobrany profile w komponentach typu ProfileTabs) zostaje. Pełny
             `window.location.replace('/')` zmusza browser do nowego requestu
             ZE ŚWIEŻYMI cookies (cleared przez confirm proxy route) -> SSR
             widzi visitor + cały client state zostaje wyczyszczony. Auto po 4s
             żeby user mógł przeczytać komunikat success. */
          setTimeout(() => {
            window.location.replace("/");
          }, 4000);
        } else {
          setMessage(
            "Procedura usunięcia konta została anulowana. Twoje konto jest bezpieczne. Jeśli to nie Ty zainicjowałeś usunięcie — zmień hasło i włącz 2FA.",
          );
        }
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMessage(e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [action, token, userIdEncrypted, router]);

  const title =
    action === "delete-account"
      ? "Usuwanie konta"
      : "Anulowanie usunięcia konta";

  const buttonLabel =
    action === "delete-account"
      ? "Strona główna"
      : "Wróć do konta";

  return (
    <div className={styles.root}>
      <div className={styles.form}>
        <h2 className={styles.formTitle}>{title}</h2>

        {status === "loading" && (
          <FormStatus variant="info">Przetwarzanie…</FormStatus>
        )}
        {status === "success" && (
          <FormStatus variant={action === "delete-account" ? "warning" : "success"}>
            {message}
            {action === "delete-account" && maskedEmail && (
              <>
                <br />
                <br />
                Email potwierdzający wysłany na: <strong>{maskedEmail}</strong>
              </>
            )}
          </FormStatus>
        )}
        {status === "error" && (
          <FormStatus variant="error">{message}</FormStatus>
        )}

        {status !== "loading" && (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => {
              if (action === "delete-account" && status === "success") {
                /* Hard nav identycznie jak auto-redirect z useEffect powyżej -
                   gwarantuje że wszystkie SSR caches + client state znikają. */
                window.location.replace("/");
              } else {
                router.push(window.location.pathname);
              }
            }}
          >
            {buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Verify-account landing (activation link from email) ────────────── */

/**
 * Auto-calls `/api/iiapi/verify` on mount to activate the account.
 * Parametry checksum i user_id pobierane z query string (backend substytuje
 * placeholdery {checksum}/{id_user} w custom_verify_url przy rejestracji).
 * Dziala BEZ zalogowania — token-only auth przez checksum.
 */
function VerifyAccountLanding() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const checksum = searchParams?.get("checksum") ?? "";
  const userIdRaw = searchParams?.get("user_id") ?? "";

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (!checksum || !userIdRaw) {
      setStatus("error");
      setMessage("Nieprawidlowy link aktywacyjny - brakuje parametrow checksum lub user_id.");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const url = `/api/iiapi/verify?checksum=${encodeURIComponent(checksum)}&user_id=${encodeURIComponent(userIdRaw)}`;
        const res = await fetch(url, { method: "GET", cache: "no-store" });
        const body = (await res.json().catch(() => null)) as {
          ok?: boolean;
          message?: string;
          verified?: boolean;
        } | null;

        if (cancelled) return;

        if (!res.ok || !body?.ok) {
          setStatus("error");
          setMessage(body?.message ?? "Aktywacja konta nie powiodła się. Link mógł wygasnąć lub być już użyty - wygeneruj nowy link po zalogowaniu.");
          return;
        }

        setStatus("success");
        setMessage("Konto zostało aktywowane. Możesz się teraz zalogować.");
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMessage(e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checksum, userIdRaw]);

  return (
    <div className={styles.root}>
      <div className={styles.form}>
        <h2 className={styles.formTitle}>Aktywacja konta</h2>

        {status === "loading" && (
          <FormStatus variant="info">Przetwarzanie...</FormStatus>
        )}
        {status === "success" && (
          <FormStatus variant="success">{message}</FormStatus>
        )}
        {status === "error" && (
          <FormStatus variant="error">{message}</FormStatus>
        )}

        {status !== "loading" && (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => router.push(window.location.pathname)}
          >
            Zaloguj się
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Verify + set-password landing (konto założone bez hasła) ────────── */

/**
 * Landing linku z maila dla konta założonego BEZ hasła - dziś zakłada je zamówienie
 * usługi ze strony z ofertą, wcześniej robił to admin z panelu.
 *
 * Backend ma na to JEDEN endpoint (`verifyUserAndSetPassword`): sprawdza token
 * `verify_account_set_password`, aktywuje konto i od razu zwraca adres formularza
 * ustawienia hasła. Ten landing tylko go woła i przenosi użytkownika pod zwrócony
 * adres - stąd brak własnego formularza.
 *
 * `custom_reset_url` budujemy TUTAJ, bo tylko front zna swój origin i ścieżkę strony
 * konta; backend podstawia w nim `{checksum}` i `{id_user}`.
 */
function VerifySetPasswordLanding({
  checksum,
  userId,
  t,
}: {
  checksum: string;
  userId: number;
  t: CUserAccountsTranslations;
}) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);

  //brak parametrów rozpoznajemy przy renderze, nie w efekcie - to wniosek z propsów,
  //a nie zdarzenie, na które trzeba czekać
  const invalidLink = !checksum || !Number.isInteger(userId) || userId <= 0;

  useEffect(() => {
    if (invalidLink) return;

    let cancelled = false;

    void (async () => {
      try {
        const resetUrl =
          `${window.location.origin}${window.location.pathname}` +
          `?action=reset-password&token={checksum}&user_id={id_user}`;

        const res = await fetch("/api/iiapi/verify-set-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ checksum, user_id: userId, custom_reset_url: resetUrl }),
          cache: "no-store",
        });
        const body = (await res.json().catch(() => null)) as {
          ok?: boolean;
          message?: string;
          data?: { reset_url?: string };
        } | null;

        if (cancelled) return;

        if (!res.ok || body?.ok !== true || !body.data?.reset_url) {
          setError(body?.message ?? t.verifySetPassword.failed);
          return;
        }

        //adres wraca z naszej domeny (zbudowaliśmy go wyżej), więc zostajemy w routerze
        router.replace(body.data.reset_url);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [invalidLink, checksum, userId, router, t]);

  const shownError = invalidLink ? t.verifySetPassword.invalidLink : error;

  return (
    <div className={styles.form}>
      <h2 className={styles.formTitle}>{t.verifySetPassword.title}</h2>
      {shownError ? (
        <FormStatus variant="error">{shownError}</FormStatus>
      ) : (
        <FormStatus variant="info">{t.verifySetPassword.pending}</FormStatus>
      )}
    </div>
  );
}

export type CUserAccountsProps = {
  content: CUserAccountsContent;
  /** SSR-resolved session — `null` gdy visitor. Wstrzykiwane przez
      `CatalogRenderer` z `page.tsx`. */
  userSession: FrontendUserSession | null;
};

/* ─── Logged-in view ─────────────────────────────────────────────────── */

function LoggedInView({
  content,
  userSession,
  t,
}: {
  content: CUserAccountsContent;
  userSession: FrontendUserSession | null;
  t: CUserAccountsTranslations;
}) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Resolve user info: SSR session prop wins, fallback do backend payload. */
  const sessionUser = userSession?.user
    ?? (content.session as CUserAccountsSession | null)?.user
    ?? null;

  if (!sessionUser) {
    /* Defensive fallback — kombinacja rzadka (loggedIn=true ale brak user
       info w żadnym źródle). Wracamy do LoggedOutView żeby user mógł się
       zalogować zamiast widzieć pusty card. */
    return <LoggedOutView content={content} t={t} />;
  }

  async function handleLogout() {
    if (busy) return;
    setBusy(true);
    setError(null);

    try {
      const res = await fetch("/api/iiapi/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const body = await safeJson(res);
        throw new Error(asMessage(body) ?? t.logout.failed);
      }
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <ProcedureGuard onLogout={handleLogout} logoutBusy={busy}>
      {/* Dokończenie powiązania z login.gov.pl, gdy osoba wybrała "połącz" i zalogowała się hasłem. */}
      <LogingovPendingLinker />
      {/* MfaGuard wewnątrz ProcedureGuard: procedury po terminie mają wyższy
          priorytet niż konfiguracja MFA (procedura może być akceptacją
          regulaminu itp., trzeba ją załatwić najpierw). Bramki niezależne;
          gdy obie aktywne, user widzi twardą blokadę procedur pierwszą. */}
      <MfaGuard>
        <ProfileTabs
          content={content}
          userSession={userSession}
          onLogout={handleLogout}
          logoutBusy={busy}
          logoutError={error}
        />
      </MfaGuard>
    </ProcedureGuard>
  );
}

/* ─── Logged-out view (login + register tabs) ────────────────────────── */

function LoggedOutView({
  content,
  t,
}: {
  content: CUserAccountsContent;
  t: CUserAccountsTranslations;
}) {
  /* URL query `?action=...` -> view:
       - `reset-password` (z linku w mailu resetu hasła) -> ResetPasswordForm
       - `remind-password` (z `<Link>` w WUserAccount modal / "Zapomniałem hasła") -> RemindPasswordForm
       - `register` (z `<Link>` w WUserAccount modal / "Załóż konto") -> tab "Załóż konto"
       - inne / brak -> domyślny LoginForm */
  const searchParams = useSearchParams();
  const showRegisterTab = content.config.user_registration_enabled;
  const action = searchParams?.get("action") ?? null;

  const [view, setView] = useState<LoggedOutViewKey>(() => {
    if (action === "reset-password") return "reset";
    if (action === "verify-set-password") return "verify-set-password";
    if (action === "remind-password") return "remind";
    if (action === "register" && showRegisterTab) return "register";
    return "login";
  });

  /* Re-sync view gdy URL `action` zmieni się — Next.js shallow navigation
     (np. user przyszedł z `/pl/konto` na `/pl/konto?action=register` przez
     `<Link>` z WUserAccount modal) NIE re-mount'uje LoggedOutView, więc
     `useState` init odpalił się raz z poprzednim action. useEffect łapie
     każdą zmianę URL. BRAK fallbacku na "login" — gdyby był, user który
     kliknął tab "Zaloguj się" wewnątrz CUserAccounts (URL bez action) byłby
     resetowany na login po każdym re-renderze. */
  useEffect(() => {
    if (action === "reset-password") setView("reset");
    else if (action === "verify-set-password") setView("verify-set-password");
    else if (action === "remind-password") setView("remind");
    else if (action === "register" && showRegisterTab) setView("register");
  }, [action, showRegisterTab]);

  /* Link z maila rejestracyjnego konta założonego BEZ hasła (np. przez zamówienie
     usługi). Aktywacja i przejście do formularza hasła to jeden krok po stronie
     backendu, więc landing tylko go woła i przenosi pod zwrócony adres. */
  if (view === "verify-set-password") {
    return (
      <div className={styles.root}>
        <VerifySetPasswordLanding
          checksum={searchParams?.get("checksum") ?? ""}
          userId={Number(searchParams?.get("user_id") ?? "0")}
          t={t}
        />
      </div>
    );
  }

  /* Reset password mode — pełny ekran formularza, bez tabsów (user przyszedł
     z konkretnego linku, nie chce widzieć opcji login/register). */
  if (view === "reset") {
    const token = searchParams?.get("token") ?? "";
    const userId = Number(searchParams?.get("user_id") ?? "0");
    return (
      <div className={styles.root}>
        <ResetPasswordForm
          content={content}
          token={token}
          userId={userId}
          onSuccess={() => setView("login")}
          t={t}
        />
      </div>
    );
  }

  /* Remind password — bez tabsów, z linkiem "← Wróć do logowania". */
  if (view === "remind") {
    return (
      <div className={styles.root}>
        <RemindPasswordForm
          content={content}
          onBack={() => setView("login")}
          t={t}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Powrót z login.gov.pl osoby bez konta (`?logingov=choose`): wybór "załóż konto"
          albo "połącz" - przy "połącz" zostaje formularz logowania hasłem. */}
      <LogingovChooser onLinkMode={() => setView("login")} />

      {showRegisterTab && (
        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={view === "login"}
            className={view === "login" ? styles.tabActive : styles.tab}
            onClick={() => setView("login")}
          >
            {content.lang.actions?.login ?? t.actions.login}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "register"}
            className={view === "register" ? styles.tabActive : styles.tab}
            onClick={() => setView("register")}
          >
            {content.lang.actions?.register ?? t.actions.register}
          </button>
        </div>
      )}

      {view === "login" ? (
        <LoginForm
          content={content}
          onForgotPassword={() => setView("remind")}
          t={t}
        />
      ) : (
        <RegisterForm content={content} onSuccess={() => setView("login")} t={t} />
      )}
    </div>
  );
}

type LoggedOutViewKey = "login" | "register" | "remind" | "reset" | "verify-set-password";

/* ─── Login form ─────────────────────────────────────────────────────── */

type LoginFormValues = {
  login: string;
  password: string;
  remember_me?: boolean;
};

function buildLoginSchema(t: CUserAccountsTranslations) {
  return z.object({
    login: z.string().min(1, t.validation.loginRequired),
    password: z.string().min(1, t.validation.passwordRequired),
    remember_me: z.boolean().optional(),
  });
}

function LoginForm({
  content,
  onForgotPassword,
  t,
}: {
  content: CUserAccountsContent;
  onForgotPassword: () => void;
  t: CUserAccountsTranslations;
}) {
  const router = useRouter();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const lang = content.lang;
  const loginSchema = buildLoginSchema(t);
  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    defaultValues: { login: "", password: "", remember_me: false },
  });
  const [topError, setTopError] = useState<string | null>(null);

  /* `?continue=` z gate'u after_login (`CatalogLoginRequired`) - po zalogowaniu
     wracamy na chronioną stronę zamiast tylko odświeżać. Dopuszczamy WYŁĄCZNIE
     wewnętrzne ścieżki (`/...`, nie `//host`) - ochrona przed open-redirect.
     Współdzielone przez login e-mail/hasło (onSubmit) ORAZ OAuth (FB/Google). */
  const continueParam = searchParams?.get("continue") ?? null;
  const safeContinue =
    continueParam &&
    continueParam.startsWith("/") &&
    !continueParam.startsWith("//")
      ? continueParam
      : null;

  /* MFA modal state - aktywne gdy backend zwrocil `mfa_required: true` z /login.
     Cookie `imperial_mfa_challenge` juz ustawiony przez route handler, modal
     wysyla kod przez /api/iiapi/login/mfa. */
  const [showMfaModal, setShowMfaModal] = useState(false);

  //Wspolna finalizacja post-login (SSO plant + redirect) - dzielona miedzy
  //normalny login (bez MFA) i sukces `<AuthMfaModal>`. Bez tego pattern by
  //sie rozjezdzal (mfa login flow musialby duplikowac SSO/redirect).
  const finishLoginRedirect = (plant?: { ticket: string; action: string }) => {
    if (plant?.ticket && plant?.action) {
      const plantContinue = safeContinue
        ? new URL(safeContinue, window.location.origin).href
        : window.location.href;
      if (submitSsoPlant(plant.action, plant.ticket, plantContinue)) {
        return;
      }
    }
    if (safeContinue) {
      router.push(safeContinue);
    } else {
      router.refresh();
    }
  };

  const onSubmit = async (values: LoginFormValues) => {
    setTopError(null);

    try {
      const res = await fetch("/api/iiapi/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login: values.login.trim(),
          password: values.password,
          remember_me: values.remember_me === true,
        }),
      });
      const body = (await safeJson(res)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
        plant?: { ticket: string; action: string };
        mfa_required?: boolean;
      } | null;

      if (!res.ok) {
        /* Per-pole server errors (gdy backend zwróci `errors: {...}`). */
        if (body?.errors && Object.keys(body.errors).length > 0) {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name as keyof LoginFormValues, { type: "server", message: msg });
            }
          }
          return;
        }
        /* Auth error nie wskazuje na konkretne pole (security: nie ujawniamy
           czy zły login czy hasło) -> top banner. */
        setTopError(body?.message ?? t.login.failed);
        return;
      }

      //MFA required - otwieramy modal z code input'em. Route handler juz ustawil
      //cookie challenge, modal wysyla kod przez /api/iiapi/login/mfa. Po sukcesie
      //modal callback -> finishLoginRedirect (SSO + redirect wspolnie z direct
      //login).
      if (body?.mfa_required === true) {
        setShowMfaModal(true);
        return;
      }

      finishLoginRedirect(body?.plant);
    } catch (err) {
      setTopError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
    {showMfaModal && (
      <AuthMfaModal
        onSuccess={(result) => {
          setShowMfaModal(false);
          finishLoginRedirect(result.plant);
        }}
        onCancel={() => {
          setShowMfaModal(false);
          //Nie ustawiamy top error - user wraca do login form bez komunikatu
          //bledu (celowe anulowanie, nie fail).
        }}
      />
    )}
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h2 className={styles.formTitle}>
        {lang.actions?.login ?? t.actions.login}
      </h2>

      <Field
        label={lang.labels?.login ?? t.labels.login}
        required
        error={errors.login?.message}
      >
        <Input
          type="text"
          autoComplete="username"
          disabled={isSubmitting}
          {...register("login")}
        />
      </Field>

      <Field
        label={lang.labels?.pass ?? t.labels.pass}
        required
        error={errors.password?.message}
      >
        <Input
          type="password"
          autoComplete="current-password"
          disabled={isSubmitting}
          {...register("password")}
        />
      </Field>

      <Controller
        name="remember_me"
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={field.value === true}
            onChange={(e) => field.onChange(e.target.checked)}
            onBlur={field.onBlur}
            name={field.name}
            ref={field.ref}
            disabled={isSubmitting}
          >
            {lang.labels?.remember_me ?? t.labels.rememberMe}
          </Checkbox>
        )}
      />

      {topError && <FormStatus variant="error">{topError}</FormStatus>}

      <SubmitButton loading={isSubmitting}>
        {lang.actions?.login ?? t.actions.login}
      </SubmitButton>

      <SubmitButton
        variant="link"
        type="button"
        onClick={onForgotPassword}
        disabled={isSubmitting}
      >
        {lang.actions?.remind_pass ?? t.actions.remindPass}
      </SubmitButton>

      {(() => {
        /* 3-poziomowy gating dla każdego providera:
             (1) rozszerzenie systemowe enabled per-domena
             (2) AKTYWNA integracja w panelu Integracje z wypełnionymi kluczami
                 (oba sprawdzane przez backend -> content.auth_providers.*.enabled)
             (3) per-katalog admin toggle (content.config.enable_*_login)
           Renderujemy `<OAuthButtons>` z filtered `providers` — gdy
           per-katalog flag wyłączony, `enabled: false` zostaje przekazany
           DALEJ (komponent sam pominie button + nie ładuje SDK). */
        const authProviders = content.auth_providers;
        const effectiveProviders = {
          facebook: {
            enabled: authProviders.facebook.enabled && content.config.enable_facebook_login,
            app_id: authProviders.facebook.app_id,
          },
          google: {
            enabled: authProviders.google.enabled && content.config.enable_google_login,
            client_id: authProviders.google.client_id,
          },
          logingov: {
            enabled: !!authProviders.logingov?.enabled && !!content.config.enable_logingov_login,
            login_url: authProviders.logingov?.login_url ?? null,
          },
        };
        return (
          <OAuthButtons
            providers={effectiveProviders}
            userGroups={content.user_groups ?? []}
            sLang={locale}
            continueUrl={safeContinue}
          />
        );
      })()}
    </form>
    </>
  );
}

/* ─── Register form ──────────────────────────────────────────────────── */

/**
 * Buduje schema dla rejestracji — checkbox'y RODO/regulamin gated po
 * config admin'a (zaznaczone jako required tylko gdy włączone).
 */
function buildRegisterSchema(
  config: CUserAccountsContent["config"],
  requirements: PasswordRequirementsInput,
  t: CUserAccountsTranslations,
) {
  return z
    .object({
      id_user_group: z.number().int().nonnegative(),
      email: z
        .string()
        .min(1, t.validation.emailRequired)
        .email(t.validation.invalidEmail),
      pass: z
        .string()
        .min(1, t.validation.passwordRegRequired)
        .regex(buildPasswordRegex(requirements), t.validation.passwordRequirements),
      ppass: z.string().min(1, t.validation.passwordRepeatRequired),
      regulamin: config.checkbox_accept_regulamin
        ? z.literal(true, { message: t.validation.requiredRegulamin })
        : z.boolean().optional(),
      personal_data: config.show_personal_data_checkbox
        ? z.literal(true, { message: t.validation.requiredPersonalData })
        : z.boolean().optional(),
      marketing: z.boolean().optional(),
    })
    .refine((v) => v.pass === v.ppass, {
      message: t.validation.passwordMismatch,
      path: ["ppass"],
    });
}

function RegisterForm({
  content,
  onSuccess,
  t,
}: {
  content: CUserAccountsContent;
  onSuccess: () => void;
  t: CUserAccountsTranslations;
}) {
  const lang = content.lang;
  const config = content.config;
  const userGroups: CUserAccountsUserGroup[] = content.user_groups ?? [];
  const passwordRequirements = content.password_requirements;

  const schema = buildRegisterSchema(config, passwordRequirements, t);
  type RegisterFormValues = z.infer<typeof schema>;

  const {
    register: registerField,
    handleSubmit,
    control,
    setError,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      id_user_group: userGroups[0]?.id ?? 0,
      email: "",
      pass: "",
      ppass: "",
      regulamin: false,
      personal_data: false,
      marketing: false,
    },
  });
  /* Live password value dla strength meter. `watch` re-rendeuje przy każdym
     keystroku — meter aktualizuje pasek i listę wymagań w czasie rzeczywistym. */
  const passwordValue = watch("pass") ?? "";
  const passwordLabels = buildPasswordStrengthLabels(t, passwordRequirements);
  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState<ReactNode | null>(null);
  /* `success` może być "happy path" (mail wysłany, variant=success) lub
     "soft failure" (konto utworzone ale SMTP padł, variant=warning). Render
     w JSX warunkowy — `mailSent === false` -> żółty `<FormStatus warning>`. */
  const [mailSent, setMailSent] = useState<boolean>(true);

  const onSubmit = async (values: RegisterFormValues) => {
    setTopError(null);
    setSuccess(null);
    setMailSent(true);

    /* Build custom_verify_url — backend wyśle ten URL w mailu aktywacyjnym,
       substytując {checksum} i {id_user} placeholdery. CUserAccounts wykrywa
       `?action=verify-account&checksum=X&user_id=Y` przez useSearchParams. */
    const customVerifyUrl =
      `${window.location.origin}${window.location.pathname}` +
      `?action=verify-account&checksum={checksum}&user_id={id_user}`;

    try {
      const res = await fetch("/api/iiapi/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, custom_verify_url: customVerifyUrl }),
      });
      const body = (await safeJson(res)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
        mail_sent?: boolean;
        mail_error?: string | null;
      } | null;

      if (!res.ok) {
        if (body?.errors && Object.keys(body.errors).length > 0) {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name as keyof RegisterFormValues, {
                type: "server",
                message: msg,
              });
            }
          }
          return;
        }
        setTopError(body?.message ?? t.register.failed);
        return;
      }

      /* Sprawdź stan wysyłki maila aktywacyjnego — backend (`IUSER::quickRegister`)
         propaguje `mail_sent: false` gdy SMTP padnie. Konto JEST utworzone
         (validacja sukces), ale user nie dostał linku — pokazujemy WARNING
         zamiast SUCCESS żeby user wiedział że musi reagować inaczej niż
         czekać na email. */
      const mailOk = body?.mail_sent !== false;
      setMailSent(mailOk);

      const template = mailOk
        ? t.register.successWithEmail
        : t.register.successMailFailed;
      const [beforeEmail, afterEmail] = template.split("{email}");
      setSuccess(
        <>
          {beforeEmail}
          <strong>{values.email}</strong>
          {afterEmail}
        </>,
      );

      /* Reset formularza do defaultValues — pola czyszczą się po sukcesie żeby
         user nie próbował submitować tego samego konta drugi raz, a komunikat
         success ZAWIERA już email użytkownika (`<strong>{values.email}</strong>`
         wyżej), więc nie tracimy informacji. Reset MUSI być po `setSuccess`
         bo `values.email` musi pozostać dostępne dla template'a. */
      reset();

      /* Bez auto-redirect — user musi mieć czas przeczytać komunikat
         (zwłaszcza warning gdy SMTP padnie — user musi wiedzieć z kim się
         skontaktować). Wcześniejszy `setTimeout(onSuccess, 2500)` ukrywał
         instrukcję; teraz user sam klika "Zaloguj się" gdy gotowy. */
    } catch (err) {
      setTopError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!config.user_registration_enabled) {
    return (
      <div className={styles.empty}>
        {lang.register_disabled ?? t.registerDisabled}
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h2 className={styles.formTitle}>
        {lang.actions?.register ?? t.actions.register}
      </h2>

      {userGroups.length > 1 && (
        <Field
          label={lang.labels?.user_group ?? t.labels.userGroup}
          required
          error={errors.id_user_group?.message}
        >
          <Select
            disabled={isSubmitting}
            {...registerField("id_user_group", { valueAsNumber: true })}
          >
            {userGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </Select>
        </Field>
      )}

      <Field
        label={lang.labels?.email ?? t.labels.email}
        required
        error={errors.email?.message}
      >
        <Input
          type="email"
          autoComplete="email"
          disabled={isSubmitting}
          {...registerField("email")}
        />
      </Field>

      <div className={styles.fieldRow}>
        <Field
          label={lang.labels?.pass ?? t.labels.passwordRegister}
          required
          error={errors.pass?.message}
        >
          <Input
            type="password"
            autoComplete="new-password"
            disabled={isSubmitting}
            {...registerField("pass")}
          />
        </Field>

        <Field
          label={lang.labels?.ppass ?? t.labels.ppass}
          required
          error={errors.ppass?.message}
        >
          <Input
            type="password"
            autoComplete="new-password"
            disabled={isSubmitting}
            {...registerField("ppass")}
          />
        </Field>
      </div>

      <PasswordStrengthMeter
        password={passwordValue}
        requirements={passwordRequirements}
        labels={passwordLabels}
      />

      {config.checkbox_accept_regulamin && (
        <Controller
          name="regulamin"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value === true}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              disabled={isSubmitting}
              error={
                typeof errors.regulamin?.message === "string"
                  ? errors.regulamin.message
                  : undefined
              }
            >
              {config.regulamin_text ? (
                <span dangerouslySetInnerHTML={{ __html: config.regulamin_text }} />
              ) : (
                <>
                  {lang.labels?.regulamin ?? t.labels.regulamin}
                  {config.regulamin_address && (
                    <>
                      {" "}
                      (
                      <a
                        href={config.regulamin_address}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {lang.labels?.regulamin_link ?? t.labels.regulaminLink}
                      </a>
                      )
                    </>
                  )}
                </>
              )}
            </Checkbox>
          )}
        />
      )}

      {config.show_personal_data_checkbox && (
        <Controller
          name="personal_data"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value === true}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              disabled={isSubmitting}
              error={
                typeof errors.personal_data?.message === "string"
                  ? errors.personal_data.message
                  : undefined
              }
            >
              {config.personal_data_text ? (
                <span
                  dangerouslySetInnerHTML={{ __html: config.personal_data_text }}
                />
              ) : (
                lang.labels?.personal_data ?? t.labels.personalData
              )}
            </Checkbox>
          )}
        />
      )}

      {config.marketing_checkbox && (
        <Controller
          name="marketing"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value === true}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              disabled={isSubmitting}
            >
              {config.marketing_text ? (
                <span dangerouslySetInnerHTML={{ __html: config.marketing_text }} />
              ) : (
                lang.labels?.marketing ?? t.labels.marketing
              )}
            </Checkbox>
          )}
        />
      )}

      {topError && <FormStatus variant="error">{topError}</FormStatus>}
      {success && (
        <FormStatus variant={mailSent ? "success" : "warning"}>
          {success}
        </FormStatus>
      )}

      <SubmitButton loading={isSubmitting}>
        {lang.actions?.register ?? t.actions.register}
      </SubmitButton>

      {content.recaptcha.enabled && (
        <p className={styles.formHint}>
          {t.register.recaptchaHint}
        </p>
      )}
    </form>
  );
}

/* ─── Remind password form (Zapomniałem hasła) ────────────────────────── */

type RemindFormValues = { email_or_login: string };

function buildRemindSchema(t: CUserAccountsTranslations) {
  return z.object({
    email_or_login: z.string().min(1, t.validation.emailOrLoginRequired),
  });
}

function RemindPasswordForm({
  content,
  onBack,
  t,
}: {
  content: CUserAccountsContent;
  onBack: () => void;
  t: CUserAccountsTranslations;
}) {
  const lang = content.lang;
  const remindSchema = buildRemindSchema(t);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RemindFormValues>({
    resolver: zodResolver(remindSchema),
    mode: "onTouched",
    defaultValues: { email_or_login: "" },
  });
  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (values: RemindFormValues) => {
    setTopError(null);

    /* Build custom_reset_url — backend wyśle ten URL w mailu, substituując
       `{checksum}` i `{id_user}` placeholders. CUserAccounts wykrywa
       `?action=reset-password&token=X&user_id=Y` przez useSearchParams. */
    const customResetUrl =
      `${window.location.origin}${window.location.pathname}` +
      `?action=reset-password&token={checksum}&user_id={id_user}`;

    try {
      const res = await fetch("/api/iiapi/init-reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_or_login: values.email_or_login.trim(),
          custom_reset_url: customResetUrl,
        }),
      });
      const body = (await safeJson(res)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
      } | null;

      if (!res.ok) {
        if (body?.errors && Object.keys(body.errors).length > 0) {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name as keyof RemindFormValues, {
                type: "server",
                message: msg,
              });
            }
          }
          return;
        }
        setTopError(body?.message ?? t.remind.failed);
        return;
      }

      setSuccess(body?.message ?? t.remind.successDefault);
    } catch (err) {
      setTopError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h2 className={styles.formTitle}>
        {lang.remind_pass ?? t.remind.title}
      </h2>

      <p className={styles.formHint}>{t.remind.hint}</p>

      <Field
        label={lang.labels?.login ?? t.labels.emailOrLogin}
        required
        error={errors.email_or_login?.message}
      >
        <Input
          type="text"
          autoComplete="username"
          disabled={isSubmitting || !!success}
          {...register("email_or_login")}
        />
      </Field>

      {topError && <FormStatus variant="error">{topError}</FormStatus>}
      {success && <FormStatus variant="success">{success}</FormStatus>}

      <SubmitButton loading={isSubmitting} disabled={!!success}>
        {t.remind.submit}
      </SubmitButton>

      <SubmitButton
        variant="link"
        type="button"
        onClick={onBack}
        disabled={isSubmitting}
      >
        ← {lang.actions?.login ?? t.actions.backToLogin}
      </SubmitButton>
    </form>
  );
}

/* ─── Reset password form (link z emaila) ──────────────────────────────── */

type ResetPasswordValues = { new_pass: string; new_ppass: string };

function buildResetPasswordSchema(
  requirements: PasswordRequirementsInput,
  t: CUserAccountsTranslations,
) {
  return z
    .object({
      new_pass: z
        .string()
        .min(1, t.validation.newPassRequired)
        .regex(buildPasswordRegex(requirements), t.validation.passwordRequirements),
      new_ppass: z.string().min(1, t.validation.newPpassRequired),
    })
    .refine((v) => v.new_pass === v.new_ppass, {
      message: t.validation.passwordMismatch,
      path: ["new_ppass"],
    });
}

function ResetPasswordForm({
  content,
  token,
  userId,
  onSuccess,
  t,
}: {
  content: CUserAccountsContent;
  token: string;
  userId: number;
  onSuccess: () => void;
  t: CUserAccountsTranslations;
}) {
  const lang = content.lang;
  const passwordRequirements = content.password_requirements;
  const resetPasswordSchema = buildResetPasswordSchema(passwordRequirements, t);
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onTouched",
    defaultValues: { new_pass: "", new_ppass: "" },
  });
  const passwordValue = watch("new_pass") ?? "";
  const passwordLabels = buildPasswordStrengthLabels(t, passwordRequirements);
  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (values: ResetPasswordValues) => {
    setTopError(null);

    try {
      const res = await fetch("/api/iiapi/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          user_id: userId,
          new_pass: values.new_pass,
          new_ppass: values.new_ppass,
        }),
      });
      const body = (await safeJson(res)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
      } | null;

      if (!res.ok) {
        if (body?.errors && Object.keys(body.errors).length > 0) {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name as keyof ResetPasswordValues, {
                type: "server",
                message: msg,
              });
            }
          }
          return;
        }
        setTopError(body?.message ?? t.reset.failed);
        return;
      }

      setSuccess(body?.message ?? t.reset.successDefault);
      /* Po sukcesie po krótkiej pauzie -> switch na login form. */
      setTimeout(onSuccess, 2500);
    } catch (err) {
      setTopError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!token || userId <= 0) {
    return (
      <div className={styles.form}>
        <FormStatus variant="error">{t.reset.invalidLink}</FormStatus>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h2 className={styles.formTitle}>{t.reset.title}</h2>

      <Field
        label={lang.labels?.pass ?? t.reset.newPass}
        required
        error={errors.new_pass?.message}
      >
        <Input
          type="password"
          autoComplete="new-password"
          disabled={isSubmitting || !!success}
          {...register("new_pass")}
        />
      </Field>

      <Field
        label={lang.labels?.ppass ?? t.reset.repeatPass}
        required
        error={errors.new_ppass?.message}
      >
        <Input
          type="password"
          autoComplete="new-password"
          disabled={isSubmitting || !!success}
          {...register("new_ppass")}
        />
      </Field>

      <PasswordStrengthMeter
        password={passwordValue}
        requirements={passwordRequirements}
        labels={passwordLabels}
      />

      {topError && <FormStatus variant="error">{topError}</FormStatus>}
      {success && <FormStatus variant="success">{success}</FormStatus>}

      <SubmitButton loading={isSubmitting} disabled={!!success}>
        {t.reset.submit}
      </SubmitButton>
    </form>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

async function safeJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Wyciąga string z `body.message` jeśli to string; inaczej null. */
function asMessage(body: Record<string, unknown> | null): string | null {
  const m = body?.message;
  return typeof m === "string" && m.length > 0 ? m : null;
}

/** Buduje labels dla `<PasswordStrengthMeter>` z bieżącego slice'a
 *  tłumaczeń + payloadu wymagań. Interpoluje `{min}`/`{max}` placeholders
 *  w label `length` na rzeczywiste wartości z backendu (`password_requirements`).
 *
 *  Wzorzec: caller (form) przekazuje labels jako prop, primitive nie ma
 *  własnego i18n (decoupling). */
function buildPasswordStrengthLabels(
  t: CUserAccountsTranslations,
  requirements: PasswordRequirementsInput,
): PasswordStrengthLabels {
  const lengthLabel = t.passwordStrength.requirements.length
    .replace("{min}", String(requirements.min_length))
    .replace("{max}", String(requirements.max_length));

  return {
    strengthLabel: t.passwordStrength.strengthLabel,
    levels: {
      veryWeak: t.passwordStrength.levels.veryWeak,
      weak: t.passwordStrength.levels.weak,
      fair: t.passwordStrength.levels.fair,
      good: t.passwordStrength.levels.good,
    },
    requirements: {
      length: lengthLabel,
      lowercase: t.passwordStrength.requirements.lowercase,
      uppercase: t.passwordStrength.requirements.uppercase,
      digit: t.passwordStrength.requirements.digit,
      special: t.passwordStrength.requirements.special,
    },
  };
}

/* TypeScript narrowing helper — pomocniczy dla CUserAccountsLangSlice. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _LangType = CUserAccountsLangSlice;
